import React, {useContext, useEffect, useState} from 'react';
import {Link} from "react-router";
import {Tag} from "@blueprintjs/core";

import {SearchableDataGrid} from "/src/components/tables/data-grid";
import {cameraServers, locations, organisations} from "/src/services/api";
import {timeAgo} from "/src/utils/time";
import {DataPageContext} from "/src/pages/data/context";

// -------------------------------------------------
// ----------------------------------- columns model
const DevicesModelColumns = [
    {
        key: 'name',
        name: 'Name',
        resizable: true,
        sortable: true,
        filterable: true,
        showDefaultFilter: true,
        formatter: (params) =>
            <Link to={'/devs/' + params.row.id}>
                {params.row.name}
            </Link>
    },
    {
        key: 'cameras',
        name: 'Cameras',
        width: 100,
        formatter: (params) =>
            <Link to={'/recs?dev=' + params.row.id}>
                <Tag round interactive>
                    Cameras
                </Tag>
            </Link>
    },
    {
        key: 'organisation.name',
        name: 'Organisation',
        resizable: true,
        sortable: true,
        filterable: true,
        formatter: (params) => params.row?.organisation?._id ?
            <Link to={'/orgs/' + params.row.organisation._id}>
                {params.row.organisation.name}
            </Link> : ''
    },
    {
        key: 'location.name',
        name: 'Location',
        resizable: true,
        sortable: true,
        filterable: true,
        formatter: (params) => params.row?.location?._id ?
            <Link to={'/locs/' + params.row.location._id}>
                {params.row.location.name}
            </Link> : ''
    },
    {
        key: 'location.status',
        name: 'Location Status',
        resizable: true,
        sortable: true,
        filterable: true,
        formatter: (params) => params.row?.location?.status || ''
    },
    {
        key: 'releaseChannel',
        name: 'Release',
        resizable: true,
        sortable: true,
        filterable: true,
    },
    {
        key: 'hardwareClass',
        name: 'Type',
        resizable: true,
        sortable: true,
        filterable: true,
    },
    {
        key: 'status',
        name: 'Status',
        resizable: true,
        sortable: true,
        filterable: true,
    },
    {
        key: 'createdAt',
        name: 'Created',
        width: 100,
        resizable: true,
        sortable: true,
        formatter: (params) =>
            <span title={params.row.createdAt}>{timeAgo(params.row.createdAt)}</span>,
    },
    {
        key: 'updatedAt',
        name: 'Updated',
        width: 100,
        resizable: true,
        sortable: true,
        formatter: (params) =>
            <span title={params.row.updatedAt}>{timeAgo(params.row.updatedAt)}</span>,
    },
];

const ManyDevs = ({search}) => {
    const {setLoading, setNav, setCount} = useContext(DataPageContext);
    const [data, setData] = useState(null);

    useEffect(() => {
        if (search.get('org') && search.get('loc')) {
            setLoading({status: false, location: 'Cannot filter by both organisation and location'});
            setData(null);
        } else {
            setLoading({status: true, location: "Loading devices..."});
            cameraServers.list({
                include: 'organisationDetails,locationDetails,locationDetailsWithoutTransactions',
                withinOrganisations: search.get("org") || undefined,
                withinLocations: search.get("loc") || undefined,
            }).then(async response => {
                setLoading({
                    status: true,
                    location: search.get("org")
                        ? `Loading organisation ${search.get("org")}`
                        : search.get("loc")
                            ? `Loading location ${search.get("loc")}`
                            : `Loaded ${response.length} devices`
                });

                const org = search.get('org') ? await organisations.get(search.get('org')) : undefined;
                const loc = search.get("loc") ? await locations.get(search.get("loc"), {include: "organisationDetails"}) : undefined;

                setData(response);
                setLoading({
                    status: false,
                    location: search.get('org') ?
                        response.length ?
                            response[0].organisation.name + " Devices"
                            : "No devices for this organisation"
                        : search.get('loc') ?
                            response.length ?
                                "Devices in " + response[0].location.name
                                : "No devices for this location"
                            : "All devices"
                });
                setNav({
                    left:
                        org ? [{
                                name: org.name,
                                path: "/orgs/" + org.id,
                                icon: "office",
                                subpaths: [
                                    {name: "Locations", icon: "area-of-interest", path: '/locs?org=' + org.id},
                                    {name: "Devices", icon: "desktop", path: '/devs?org=' + org.id},
                                    {name: "Cameras", icon: "camera", path: '/recs?org=' + org.id},
                                    {name: "Users", icon: "people", path: '/orgs/' + org.id + '/users'}
                                ]
                            }]
                            : loc ? [{
                                name: loc.organisation.name,
                                path: "/orgs/" + loc.organisation._id,
                                icon: "office",
                                subpaths: [
                                    {name: "Locations", icon: "area-of-interest", path: '/locs?org=' + loc.organisation._id},
                                    {name: "Devices", icon: "desktop", path: '/devs?org=' + loc.organisation._id},
                                    {name: "Cameras", icon: "camera", path: '/recs?org=' + loc.organisation._id},
                                    {name: "Users", icon: "people", path: '/orgs/' + loc.organisation._id + '/users'}
                                ]
                            }, {
                                name: loc.name,
                                path: "/locs/" + loc.id,
                                icon: "area-of-interest",
                                subpaths: [
                                    {name: "Cameras", icon:"camera", path: '/recs?loc=' + loc.id},
                                ]
                            }] : undefined,
                });

            }).catch(error => {
                setLoading({status: false, location: `Failed to load devices: ${error.message}`});
                setData(null);
            });
        }
    }, [search])

    return <SearchableDataGrid
            columns={DevicesModelColumns}
            data={data ?? []}
            defaultSort='name'
            onCount={(filtered, total) => setCount({filtered, total})}
        />
}

export {ManyDevs};
